/**
 * The assistant's chrome - drawer over any page, or the full-width section.
 *
 * This element owns no conversation of its own. It mounts `<data-assistant>`
 * exactly once and changes the frame around it, which is what makes "open the
 * full view" continue the thread rather than start a second one. See
 * `state/assistant-surface.ts` for why that is a frame swap and not two mounts.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { chatsOnServer } from './assistant-chat-store.ts';
import { activeSection, isRootUser } from '../../state/store.ts';
import { isPageVisible } from '../../state/page-visibility.ts';
import {
  assistantDrawerOpen,
  assistantDrawerHeight,
  assistantFloating,
  assistantFloatAt,
  setAssistantFloating,
  setAssistantFloatAt,
  assistantDrawerWidth,
  assistantMinimized,
  assistantTab,
  assistantTechnical,
  drawerMax,
  drawerMaxHeight,
  setAssistantDrawerHeight,
  setAssistantDrawerWidth,
  setAssistantMinimized,
  setAssistantTechnical,
  DRAWER_MIN,
  DRAWER_MIN_H,
  assistantSurface,
  closeAssistantDrawer,
  leaveAssistantSection,
  openAssistantSection,
  syncAssistantSurface,
  chiefEngineerOn,
} from '../../state/assistant-surface.ts';
import { assistantMark } from './mark.ts';
import './assistant-panel.ts';
import './history-page.ts';
import '../dashboard/dash-dropdown.ts';
import type { DashDropdown } from '../dashboard/dash-dropdown.ts';
import type { DataAssistant } from './assistant-panel.ts';
import {
  assistantAutoPlot,
  assistantChiefEngineer,
  setAssistantAutoPlot,
  setAssistantChiefEngineer,
} from '../../state/assistant-settings.ts';

@customElement('assistant-surface')
export class AssistantSurface extends LightElement {
  private store = new StoreController(this, [
    assistantDrawerOpen,
    activeSection,
    assistantTechnical,
    assistantAutoPlot,
    assistantChiefEngineer,
    isRootUser,
    assistantDrawerWidth,
    assistantDrawerHeight,
    assistantMinimized,
    assistantTab,
    assistantFloating,
    assistantFloatAt,
  ]);

  /** Where chats are stored and what is logged. */
  @state() private showNotice = false;
  /** Whether chats reach the account, so the notice says where they went. */
  @state() private chatsStored = false;

  override connectedCallback(): void {
    super.connectedCallback();
    void chatsOnServer().then((server) => {
      this.chatsStored = server;
    });
    syncAssistantSurface();
    document.addEventListener('keydown', this.onKeydown);
    this.addEventListener('open-chat', this.onOpenChat as EventListener);
  }

  /** History found a conversation; the panel is the one that holds transcripts,
   *  so the id crosses over and the panel does the rest. */
  private onOpenChat = (e: CustomEvent<string>): void => {
    this.querySelector<DataAssistant>('data-assistant')?.openChatById(e.detail);
  };

  override disconnectedCallback(): void {
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('open-chat', this.onOpenChat as EventListener);
    super.disconnectedCallback();
  }

  /**
   * Drag one of the drawer's edges.
   *
   * Both edges measure from the corner the drawer is anchored to - bottom-right
   * - so the composer and the close button stay under your hands while the
   * opposite edge moves. Pointer capture rather than window listeners, so a
   * fast drag that outruns the cursor keeps resizing instead of dropping the
   * grip.
   */
  private onGripDown(e: PointerEvent, axis: 'x' | 'y'): void {
    const grip = e.currentTarget as HTMLElement;
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    document.body.classList.add(axis === 'x' ? 'resizing-panel' : 'resizing-panel-y');
    const move = (ev: PointerEvent): void => {
      if (axis === 'x') setAssistantDrawerWidth(window.innerWidth - ev.clientX);
      else setAssistantDrawerHeight(window.innerHeight - ev.clientY);
    };
    const up = (): void => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      document.body.classList.remove('resizing-panel');
      document.body.classList.remove('resizing-panel-y');
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  /**
   * Undocked: move the window by its bar.
   *
   * Only a press on the bar itself, or on the title - a press on one of its
   * buttons is that button's click, not the start of a move.
   */
  private onBarDown(e: PointerEvent): void {
    if (!this.floating() || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button, dash-dropdown, .assistant-float-grip')) return;
    const bar = e.currentTarget as HTMLElement;
    e.preventDefault();
    bar.setPointerCapture(e.pointerId);
    const start = assistantFloatAt.value;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    document.body.classList.add('moving-assistant');
    const move = (ev: PointerEvent): void => setAssistantFloatAt(ev.clientX - dx, ev.clientY - dy);
    const up = (): void => {
      bar.removeEventListener('pointermove', move);
      bar.removeEventListener('pointerup', up);
      bar.removeEventListener('pointercancel', up);
      document.body.classList.remove('moving-assistant');
    };
    bar.addEventListener('pointermove', move);
    bar.addEventListener('pointerup', up);
    bar.addEventListener('pointercancel', up);
  }

  /** Undocked: size the window from its bottom-right corner, both axes at once.
   *  The top-left corner stays put, which is what a window does. */
  private onCornerDown(e: PointerEvent): void {
    const grip = e.currentTarget as HTMLElement;
    const frame = grip.closest<HTMLElement>('.assistant-surface');
    if (!frame) return;
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    const rect = frame.getBoundingClientRect();
    document.body.classList.add('resizing-assistant-corner');
    const move = (ev: PointerEvent): void => {
      // Never past the window's own edges: a corner dragged off-screen would
      // leave the corner itself off-screen, with nothing left to grab.
      setAssistantDrawerWidth(Math.min(ev.clientX, window.innerWidth - 4) - rect.left);
      setAssistantDrawerHeight(Math.min(ev.clientY, window.innerHeight - 4) - rect.top);
    };
    const up = (): void => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      document.body.classList.remove('resizing-assistant-corner');
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  /** Undocked, from the keyboard: arrows grow and shrink the window. */
  private onCornerKey(e: KeyboardEvent): void {
    const step = e.shiftKey ? 64 : 16;
    const h = assistantDrawerHeight.value || drawerMaxHeight();
    if (e.key === 'ArrowRight') setAssistantDrawerWidth(assistantDrawerWidth.value + step);
    else if (e.key === 'ArrowLeft') setAssistantDrawerWidth(assistantDrawerWidth.value - step);
    else if (e.key === 'ArrowDown') setAssistantDrawerHeight(h + step);
    else if (e.key === 'ArrowUp') setAssistantDrawerHeight(h - step);
    else return;
    e.preventDefault();
  }

  private floating(): boolean {
    return assistantFloating.value && assistantSurface() === 'drawer';
  }

  private newChat(): void {
    this.querySelector<DataAssistant>('data-assistant')?.startNewChat();
  }

  /** Where chats are kept, behind a circled i beside the title. On the full
   *  page the title is hidden, so the same button sits with the controls. */
  private renderInfoButton(): TemplateResult {
    return html`<button
      class="assistant-surface-btn assistant-info-btn ${this.showNotice ? 'active' : ''}"
      title="Chat storage and logging"
      aria-label="Chat storage and logging"
      aria-expanded=${this.showNotice}
      @click=${() => {
        this.showNotice = !this.showNotice;
        this.querySelector<DashDropdown>('dash-dropdown')?.close();
      }}
    >
      <!-- Drawn rather than typed: U+24D8 and every other circled-i
           codepoint is tofu in the deployed font set. -->
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="6.4" stroke="currentColor" stroke-width="1.3" />
        <path d="M8 7.1v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="8" cy="4.9" r="0.85" fill="currentColor" />
      </svg>
    </button>`;
  }

  /** The same resize from the keyboard, since a drag handle that only takes a
   *  mouse is a control some people simply do not have. */
  private onGripKey(e: KeyboardEvent, axis: 'x' | 'y'): void {
    const step = e.shiftKey ? 64 : 16;
    if (axis === 'x') {
      if (e.key === 'ArrowLeft') setAssistantDrawerWidth(assistantDrawerWidth.value + step);
      else if (e.key === 'ArrowRight') setAssistantDrawerWidth(assistantDrawerWidth.value - step);
      else return;
    } else {
      const now = assistantDrawerHeight.value || drawerMaxHeight();
      if (e.key === 'ArrowUp') setAssistantDrawerHeight(now + step);
      else if (e.key === 'ArrowDown') setAssistantDrawerHeight(now - step);
      else return;
    }
    e.preventDefault();
  }

  /** Escape dismisses the drawer, the way every other overlay in the app does.
   *  It deliberately does not leave the full section - Escape on a page you
   *  navigated to would be a surprise, not a dismissal. */
  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    // The shared dropdown handles Escape first, keeping the chat open.
    if (this.querySelector('.assistant-settings .dash-dd-trigger[aria-expanded="true"]')) return;
    if (assistantSurface() !== 'drawer') return;
    closeAssistantDrawer();
  };

  protected override updated(): void {
    const surface = assistantSurface();
    if (surface !== 'closed' && surface !== 'mark') return;
    this.querySelector<DashDropdown>('dash-dropdown')?.close();
    this.showNotice = false;
  }

  override render(): TemplateResult | typeof nothing {
    // Gated exactly like the old tab was: the flag, or the permission that sees
    // past it. With neither, there is no assistant to frame.
    if (!isPageVisible('assistant')) return nothing;
    const surface = assistantSurface();
    const drawer = surface === 'drawer';

    // Shrunk to the mark: the conversation is still here, it is simply not in
    // front of the page any more. One click brings it back at the width it had.
    return html`
      <div
        class="assistant-surface assistant-surface-${surface}"
        role=${drawer ? 'dialog' : 'region'}
        aria-label="SONAR"
        aria-hidden=${surface === 'closed' || surface === 'mark'}
        ?hidden=${surface === 'closed' || surface === 'mark'}
      >
        ${drawer && !this.floating()
          ? html`<div
                class="assistant-grip"
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize SONAR sideways"
                aria-valuenow=${assistantDrawerWidth.value}
                aria-valuemin=${DRAWER_MIN}
                aria-valuemax=${drawerMax()}
                tabindex="0"
                title="Drag to resize"
                @pointerdown=${(e: PointerEvent) => this.onGripDown(e, 'x')}
                @keydown=${(e: KeyboardEvent) => this.onGripKey(e, 'x')}
              >
                <span class="assistant-grip-line" aria-hidden="true"></span>
              </div>
              <!-- The top edge. Dragging it down shortens the drawer from the
                   top, uncovering the part of the page you are reading while
                   the composer stays where your hands are. -->
              <div
                class="assistant-grip assistant-grip-y"
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize SONAR vertically"
                aria-valuenow=${assistantDrawerHeight.value || drawerMaxHeight()}
                aria-valuemin=${DRAWER_MIN_H}
                aria-valuemax=${drawerMaxHeight()}
                tabindex="0"
                title="Drag to resize"
                @pointerdown=${(e: PointerEvent) => this.onGripDown(e, 'y')}
                @keydown=${(e: KeyboardEvent) => this.onGripKey(e, 'y')}
              >
                <span class="assistant-grip-line" aria-hidden="true"></span>
              </div>`
          : nothing}
        ${this.floating()
          ? html`<div
              class="assistant-float-grip"
              role="separator"
              aria-label="Resize the SONAR window"
              tabindex="0"
              title="Drag to resize"
              @pointerdown=${(e: PointerEvent) => this.onCornerDown(e)}
              @keydown=${(e: KeyboardEvent) => this.onCornerKey(e)}
            ></div>`
          : nothing}
        <div
          class="assistant-surface-bar"
          title=${this.floating() ? 'Drag to move' : ''}
          @pointerdown=${(e: PointerEvent) => this.onBarDown(e)}
        >
          <span class="assistant-surface-name">
            <span class="assistant-glyph">${assistantMark(18)}</span>
            SONAR
            ${drawer ? this.renderInfoButton() : nothing}
          </span>
          ${chiefEngineerOn()
              ? html`<span class="assistant-chief-chip" title="Chief Engineer mode is on: scope and style rules relaxed">Chief Engineer</span>`
              : nothing}
          <div class="assistant-surface-tools">
            ${drawer
              ? html`<button
                  class="assistant-surface-btn assistant-new-chat"
                  title="New chat"
                  aria-label="Start a new chat"
                  @click=${() => this.newChat()}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"
                      stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                    />
                  </svg>
                  <span>New</span>
                </button>`
              : nothing}
            <dash-dropdown
              class="assistant-settings"
              triggerClass="assistant-surface-btn"
              triggerTitle="SONAR settings"
              panelClass="assistant-settings-panel"
              .alignEnd=${true}
              .fixed=${true}
              .panel=${() => this.renderSettings()}
              @click=${() => { this.showNotice = false; }}
              .triggerContent=${html`
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"
                  />
                  <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8" />
                </svg>`}
            ></dash-dropdown>
            ${drawer ? nothing : this.renderInfoButton()}
            ${drawer
              ? html`<button
                    class="assistant-surface-btn"
                    title="Open the full view"
                    aria-label="Open the full view"
                    @click=${() => openAssistantSection()}
                  >
                    ⤢
                  </button>
                  <button
                    class="assistant-surface-btn"
                    title="Shrink to the corner"
                    aria-label="Shrink SONAR to the corner"
                    @click=${() => setAssistantMinimized(true)}
                  >
                    —
                  </button>`
              : nothing}
            <button
              class="assistant-surface-btn"
              title=${drawer ? 'Close' : 'Back'}
              aria-label=${drawer ? 'Close SONAR' : 'Leave SONAR'}
              @click=${() => (drawer ? closeAssistantDrawer() : leaveAssistantSection())}
            >
              ${drawer ? '✕' : '←'}
            </button>
          </div>
        </div>
        ${this.showNotice
          ? html`<div class="assistant-notice">
              <p role="note">
                ${this.chatsStored
                  ? 'Your chat history is securely stored and linked to your account. Authorized administrators may review conversations for research and to improve SONAR’s quality and reliability. Your chats are available on any device you sign in on.'
                  : 'Only you can read your chats. They stay in this browser for 30 days.'}
              </p>
            </div>`
          : nothing}
        <data-assistant ?hidden=${surface === 'full' && assistantTab.value === 'history'}>
        </data-assistant>
        ${surface === 'full' && assistantTab.value === 'history'
          ? html`<assistant-history-page></assistant-history-page>`
          : nothing}
      </div>
      ${surface === 'mark'
        ? html`<button
            class="assistant-mark-button"
            title="Open SONAR"
            aria-label="Open SONAR"
            @click=${() => setAssistantMinimized(false)}
          >${assistantMark(26)}</button>`
        : nothing}
    `;
  }

  private renderSettings(): TemplateResult {
    return html`<div class="dset-group" role="group" aria-label="SONAR settings">
      <button
        class="dset-toggle ${assistantAutoPlot.value ? 'is-on' : ''}"
        role="switch"
        aria-checked=${assistantAutoPlot.value}
        @click=${() => setAssistantAutoPlot(!assistantAutoPlot.value)}
      >
        <span class="dset-toggle-text">
          <strong>Plot replies automatically</strong>
          <span>Open new plot replies immediately. Remembered in this browser.</span>
        </span>
        <span class="sd-switch" aria-hidden="true"></span>
      </button>
      <button
        class="dset-toggle ${assistantTechnical.value ? 'is-on' : ''}"
        role="switch"
        aria-checked=${assistantTechnical.value}
        @click=${() => setAssistantTechnical(!assistantTechnical.value)}
      >
        <span class="dset-toggle-text">
          <strong>Show technical details</strong>
          <span>Tool activity, tokens and cost, and the model's reasoning.</span>
        </span>
        <span class="sd-switch" aria-hidden="true"></span>
      </button>
      <button
        class="dset-toggle ${assistantFloating.value ? 'is-on' : ''}"
        role="switch"
        aria-checked=${assistantFloating.value}
        @click=${() => setAssistantFloating(!assistantFloating.value)}
      >
        <span class="dset-toggle-text">
          <strong>Undocked window</strong>
          <span>Float SONAR as a window you move by its title bar and size from its corner. Remembered in this browser.</span>
        </span>
        <span class="sd-switch" aria-hidden="true"></span>
      </button>
      ${isRootUser.value
        ? html`<button
            class="dset-toggle assistant-chief-toggle ${assistantChiefEngineer.value ? 'is-on' : ''}"
            role="switch"
            aria-checked=${assistantChiefEngineer.value}
            @click=${() => setAssistantChiefEngineer(!assistantChiefEngineer.value)}
          >
            <span class="dset-toggle-text">
              <strong>Chief Engineer mode</strong>
              <span>Talk to SONAR unfiltered: any topic, column ids and its own reasoning, its instructions on request. Root only; turns are marked in transcripts.</span>
            </span>
            <span class="sd-switch" aria-hidden="true"></span>
          </button>`
        : nothing}
      <p class="dset-note">Exports still need Apply to Export. Existing replies open with Show me.</p>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'assistant-surface': AssistantSurface;
  }
}
